/**
 * Accessibility utilities for StatScout
 */

import { useCallback, useRef, useEffect } from 'react'

/**
 * Hook to announce messages to screen readers via aria-live region
 * @returns announce function to call with message
 */
export function useAnnounce() {
  const regionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Create or find the live region
    let region = document.getElementById('sr-announcer') as HTMLDivElement
    if (!region) {
      region = document.createElement('div')
      region.id = 'sr-announcer'
      region.setAttribute('role', 'status')
      region.setAttribute('aria-live', 'polite')
      region.setAttribute('aria-atomic', 'true')
      region.className = 'sr-only'
      document.body.appendChild(region)
    }
    regionRef.current = region

    return () => {
      // Don't remove on unmount - other components may use it
    }
  }, [])

  const announce = useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    if (regionRef.current) {
      regionRef.current.setAttribute('aria-live', politeness)
      // Clear and set to trigger announcement
      regionRef.current.textContent = ''
      // Use setTimeout to ensure the clear registers before the new content
      setTimeout(() => {
        if (regionRef.current) {
          regionRef.current.textContent = message
        }
      }, 50)
    }
  }, [])

  return announce
}

/**
 * Hook to trap focus within a container (for modals/dialogs)
 * @param isActive - Whether the trap is active
 * @returns ref to attach to the container element
 */
export function useFocusTrap<T extends HTMLElement>(isActive: boolean) {
  const containerRef = useRef<T>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isActive || !containerRef.current) return

    // Store the previously focused element
    previousFocusRef.current = document.activeElement as HTMLElement

    const container = containerRef.current
    const focusableSelector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ')

    const focusableElements = container.querySelectorAll<HTMLElement>(focusableSelector)
    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]

    // Focus the first focusable element
    if (firstFocusable) {
      firstFocusable.focus()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault()
          lastFocusable?.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault()
          firstFocusable?.focus()
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      // Restore focus to the previously focused element
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
    }
  }, [isActive])

  return containerRef
}

/**
 * Hook for keyboard navigation in lists (roving tabindex)
 * @param itemCount - Number of items in the list
 * @param orientation - 'horizontal' | 'vertical'
 * @returns { activeIndex, setActiveIndex, getTabIndex, handleKeyDown }
 */
export function useRovingTabIndex(
  itemCount: number,
  orientation: 'horizontal' | 'vertical' = 'vertical'
) {
  const activeIndexRef = useRef(0)

  const setActiveIndex = useCallback((index: number) => {
    activeIndexRef.current = Math.max(0, Math.min(index, itemCount - 1))
  }, [itemCount])

  const getTabIndex = useCallback((index: number) => {
    return index === activeIndexRef.current ? 0 : -1
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent, currentIndex: number) => {
    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'

    let newIndex = currentIndex

    switch (e.key) {
      case prevKey:
        e.preventDefault()
        newIndex = currentIndex > 0 ? currentIndex - 1 : itemCount - 1
        break
      case nextKey:
        e.preventDefault()
        newIndex = currentIndex < itemCount - 1 ? currentIndex + 1 : 0
        break
      case 'Home':
        e.preventDefault()
        newIndex = 0
        break
      case 'End':
        e.preventDefault()
        newIndex = itemCount - 1
        break
      default:
        return
    }

    setActiveIndex(newIndex)
    return newIndex
  }, [itemCount, orientation, setActiveIndex])

  return {
    activeIndex: activeIndexRef.current,
    setActiveIndex,
    getTabIndex,
    handleKeyDown,
  }
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Hook to detect reduced motion preference
 */
export function usePrefersReducedMotion(): boolean {
  const ref = useRef(prefersReducedMotion())

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    ref.current = mediaQuery.matches

    const handler = (e: MediaQueryListEvent) => {
      ref.current = e.matches
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  return ref.current
}

/**
 * Generate a unique ID for accessibility associations
 */
let idCounter = 0
export function generateA11yId(prefix = 'a11y'): string {
  return `${prefix}-${++idCounter}`
}

/**
 * Hook to generate stable IDs for form associations
 */
export function useA11yId(prefix = 'a11y'): string {
  const idRef = useRef<string>()
  if (!idRef.current) {
    idRef.current = generateA11yId(prefix)
  }
  return idRef.current
}
